import TermsOfUseIcon from '@/assets/icon-terms-of-use.svg?react';
import UserIcon from '@/assets/icon-user.svg?react';
import CategoryIcon from '@/assets/icon-category.svg?react';
import ShortcutIcon from '@/assets/icon-shortcut.svg?react';
import CheckIcon from '@/assets/icon-google-tick.svg?react';
import IconBell from '@/assets/icon_bell.svg?react';
import { Modal, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { HISTORY_ZERO_STATE } from '@/constants/HistoryConstants';
import useElysiaStore from '@/app/elysia-store';
import { splitByWords } from '@/utils/titleUtils';
import useIsMobile from '@/utils/useIsMobile';
import FilledHeartIcon from '@/assets/icon-heart-filled.svg?react';
import EmptyHeartIcon from '@/assets/icon-heart-outline.svg?react';
import { toggleAppFavoriteStatus, toggleSkillFavoriteStatus } from '@/services/aiApps';
import DefaultStarIcon from '@/assets/icon-star-default.svg?react';
import ConversationIcon from '@/assets/icon-conversation.svg?react';
import { formatCompactNumber } from '@/utils/formatCompactNumber';
import { LibraryConfig } from '../../types/LibraryConfig';
import '../../libraryStyle.scss'; // modal styles live here; needed when opened outside the library page
// add import
import { SKILL_CATEGORY_ICONS, PROMPT_CATEGORY_ICONS, OtherIcon } from '../shared/CategoryIcon';
interface SummaryModalProps {
  config: LibraryConfig;
  showModal: any;
  setShowSummaryModal: any;
  tabName: any;
  cardData: any;
  skillIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>; // optional icon for skills
}

export const SummaryModal = ({
  config,
  cardData,
  showModal,
  setShowSummaryModal,
  tabName,
}: SummaryModalProps) => {
  const isApp = config.entityType === 'app';
  const isSkill = config.entityType === 'skill';
  const entityName = config.displayName.toLowerCase();
  const {
    showRating,
    showSessions,
    showExamplePrompts = true,
    showCapabilities = true,
    showUsageGuidelines = true,
    showFavourite = true,
  } = config.modal;

  const primaryActionLabel =
    config.entityType === 'app'
      ? 'Launch app'
      : config.entityType === 'skill'
        ? 'Try Now'
        : 'Insert to Prompt Box';

  const iconMap = isSkill ? SKILL_CATEGORY_ICONS : PROMPT_CATEGORY_ICONS;
  // Skill Icon for different category
  const SkillIcon = isSkill ? (iconMap[cardData.category] ?? OtherIcon) : null;

  const closeModalRef = useRef<HTMLButtonElement>(null);
  const launchButtonRef = useRef<HTMLButtonElement>(null);
  const setAppFavourite = useElysiaStore((state) => state.setAppFavourite);
  const setSkillFavourite = useElysiaStore((state) => state.setSkillFavourite);
  const favoriteList = useElysiaStore((state) => state.favouriteAgent);
  const [markFavourite, setMarkFavourite] = useState(() => {
    if (config.entityType === 'skill') return cardData?.is_favourite ?? false;
    const favItem = favoriteList?.find((ele) => ele.agent_id === cardData?.agent_id);
    return favItem?.is_favourite ?? false;
  });
  const setAttachedFileArray = useElysiaStore((state) => state.setAttachedFileArray);
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setUploadedFile = useElysiaStore((state) => state.setUploadedFile);
  const setIsPrivateChat = useElysiaStore.getState().setIsPrivateChat;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const resetAIApp = useElysiaStore.getState().resetAIApp;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setSources = useElysiaStore.getState().setSources;
  const setChatInputSources = useElysiaStore.getState().setChatInputSources;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const isMobile = useIsMobile(767);

  const handleLaunchKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      closeModalRef.current?.focus();
    }
  };

  const navigate = useNavigate();
  const handleLaunchApp = () => {
    if (config.entityType == 'app') {
      setAttachedFileArray?.([]);
      setSelectedSources?.([]);
      setChatInputSources?.([]);
      setSources?.([]);
      setUploadedFile?.(null);
      setBrandDoc?.(false);
      setIsPrivateChat?.(false);
      setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
      setChatAgent?.('app-agent');
      setIsAppTerminated(false);
      resetAIApp();
      setIsRestrictedChatSessionStatus?.('');
      const url = cardData?.redirect_url
        ? `${cardData.redirect_url}/?app=${cardData.agent_id}`
        : `/?app=${cardData.agent_id}`;

      navigate(url, { state: { agent_id: cardData.agent_id } });
    }
  };

  useEffect(() => {
    if (showModal) {
      setTimeout(() => {
        if (closeModalRef.current) {
          closeModalRef.current.focus();
        }
      }, 0);
    }
  }, [showModal]);

  const handleToggleFavourite = async () => {
    if (isSkill) {
      const next = !markFavourite;
      setMarkFavourite(next);
      const response = await toggleSkillFavoriteStatus(cardData?.id, next);
      if (response) {
        setSkillFavourite(cardData.id, next);
      } else {
        setMarkFavourite(!next);
      }
      return;
    }

    const payload = { agent_id: cardData?.agent_id, is_favourite: !markFavourite };
    setMarkFavourite(!markFavourite);
    const response = await toggleAppFavoriteStatus(payload);
    if (response) {
      setAppFavourite(payload);
    }
  };
  return (
    <>
      <Modal
        show={showModal}
        onHide={() => setShowSummaryModal(false)}
        centered
        dialogClassName={`elysia-modal summary-modal ${isMobile && 'summary-modal-mobile'} `}
      >
        <Modal.Header
          className="border-0 p-0 d-flex align-items-center justify-content-between w-100"
          closeButton
        >
          <Modal.Title className="mb-lg-0 horizontal-alignments-mobile ">
            <p className="d-flex align-items-center mb-0">
              {cardData?.icon ? (
                <img src={cardData?.icon} className="app-icon me-2" alt="ai-app-icon" />
              ) : isSkill && SkillIcon ? (
                <SkillIcon className="skill-icon me-2" />
              ) : null}
              <h5 className="mb-0" title={splitByWords(cardData?.label, 10)}>
                {cardData.label}
              </h5>
            </p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Adding Conditional Notification Icon */}

          {!isSkill && cardData?.agent_status !== 'published' && (
            <div
              className={`notification-tooltip d-flex mb-2.5 h-auto ${isMobile && 'horizontal-alignments-mobile'}`}
            >
              <IconBell className="flex-shrink-0 bell-icon" />
              {cardData?.agent_status == 'unpublished' && tabName === 'My AI Apps' ? (
                <p className="mb-0">This {entityName} is not published yet.</p>
              ) : tabName === 'My AI Apps' && cardData?.agent_status == 'processing' ? (
                <div className="mb-0">
                  Usage and Sharing is prevented for Apps while their Knowledge files are uploaded.
                  You will receive an email when this process is complete
                </div>
              ) : (
                cardData?.agent_status === 'unpublished' && (
                  <p className="mb-0">This {entityName} is currently disabled by the owner.</p>
                )
              )}
            </div>
          )}

          <p
            className={`${isMobile ? 'horizontal-alignments-mobile' : 'card-data-descritption'}`}
            title={cardData?.description && splitByWords(cardData?.description, 5)}
          >
            {cardData?.description}
          </p>

          {(showRating || showSessions) && (
            <div
              className={`my-3 d-flex gap-3 ${
                isMobile
                  ? 'app-interactions d-flex my-3 justify-content-between horizontal-alignments-mobile'
                  : ''
              }`}
            >
              {showRating && (
                <AppStat
                  icon={DefaultStarIcon}
                  label={cardData?.total_ratings > 1 ? 'Ratings' : 'Rating'}
                  value={cardData?.average_rating}
                  emptyText="No ratings yet"
                  count={cardData?.total_ratings}
                  showCount={true}
                  isMobile={isMobile}
                />
              )}
              {showSessions && (
                <AppStat
                  icon={ConversationIcon}
                  label={cardData?.total_sessions > 1 ? 'Conversations' : 'Conversation'}
                  value={cardData?.total_sessions}
                  emptyText="No conversations yet"
                  showCount={false}
                  isMobile={isMobile}
                  title="Updates daily"
                />
              )}
            </div>
          )}

          <div
            className={`agent-app-info d-flex align-items-center my-4 ${isMobile && 'horizontal-alignments-mobile'}`}
          >
            <div
              className={`agent-info-item d-flex ${cardData?.key == 'brand_agent' ? 'me-5' : ''}`}
            >
              <CategoryIcon className="category-icon me-2" />
              <div>
                <div className="app-info-text" title={splitByWords(cardData.category)}>
                  {cardData.category}
                </div>
                <small className="subtitle">Category</small>
              </div>
            </div>
            <div className="border-start border-vertical"></div>
            <div className="agent-info-item d-flex">
              <UserIcon className="user-icon me-2" />
              <div>
                <div className="app-info-text" title={splitByWords(cardData?.created_by)}>
                  {cardData?.created_by}
                </div>
                <small className="subtitle">Created by</small>
              </div>
            </div>
            {!isMobile && showUsageGuidelines && (
              <>
                <div className="border-start border-vertical"></div>
                <div className="agent-info-item d-flex">
                  <TermsOfUseIcon className="me-2 mt-1" />
                  <div>
                    <div className="app-info-text" title="App Usage Guidelines">
                      App Usage Guidelines
                    </div>
                    <Link
                      className="terms-of-use"
                      to={cardData?.terms_of_use}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click Here
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          {isSkill && cardData?.instruction && (
            <div className={`skill-instruction ${isMobile && 'horizontal-alignments-mobile'}`}>
              <div className="prompt-text">Instructions</div>
              <div className="skill-instruction-box">{cardData.instruction}</div>
            </div>
          )}

          {/* hidden until the backend returns a shortcut */}
          {isSkill && cardData?.skill_shortcut && (
            <div className="agent-info-item d-flex my-4">
              <ShortcutIcon className="category-icon me-2" />
              <div>
                <div className="app-info-text" title={cardData.skill_shortcut}>
                  {cardData.skill_shortcut}
                </div>
                <small className="subtitle">Shortcut</small>
              </div>
            </div>
          )}

          {/* Add condition for showing exmaple promts */}
          {showExamplePrompts && cardData?.example_prompts?.length > 0 ? (
            <div
              className={`prompt-container ${isMobile && 'horizontal-alignments-mobile mb-1 me-2 w-auto'}`}
            >
              <span className="prompt-text mb-3">Example prompts</span>

              <div>
                <div className="example-prompt-card-container p-0}">
                  {cardData?.example_prompts.map((prompt, index) => (
                    <div key={index} className="example-prompt-card" title={splitByWords(prompt)}>
                      <div className="d-flex example-prompt-card-body">
                        <small className="example-prompt-text">{prompt}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            ''
          )}
          {/* Add condition for showing exmaple promts */}

          {showCapabilities &&
            cardData?.key !== 'brand_agent' &&
            cardData?.capabilities?.['web_search'] && (
              <div className={`capabilities ${isMobile && 'horizontal-alignments-mobile mb-0'}`}>
                <div className="capibility-text">Capabilities</div>
                <p>
                  <span className="check-icon">
                    <CheckIcon className="me-2" />
                  </span>
                  <span>Web Search</span>
                </p>
              </div>
            )}

          {/* kind-specific extra section from the config */}
          {config.extraModalContent?.(cardData)}

          {showUsageGuidelines && (
            <div className="d-md-none">
              <div className="app-builder-terms ms-sm-4 mb-4">
                Learn more about our
                <Link
                  className="ms-1"
                  to={cardData?.terms_of_use}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  App Usage Guidelines
                </Link>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          className={`justify-content-between border-0 p-0 ${isMobile && 'horizontal-alignments-mobile'}`}
        >
          <Button onClick={() => setShowSummaryModal(false)} className="m-0" variant="secondary">
            Cancel
          </Button>

          {(isApp || isSkill) && showFavourite && tabName !== 'in-app' && (
            <Button
              className={`ms-auto me-2 favourite-btn-icon ${markFavourite && 'active'}`}
              onClick={handleToggleFavourite}
              disabled={isApp && cardData?.agent_status != 'published'}
              variant="secondary"
              aria-label="favourite-icon"
            >
              <span>{markFavourite ? <FilledHeartIcon /> : <EmptyHeartIcon />}</span>
            </Button>
          )}
          {
            <Button
              ref={launchButtonRef}
              onKeyDown={handleLaunchKeyDown}
              onClick={handleLaunchApp}
              variant="primary"
              disabled={
                config.entityType != 'skill' &&
                (cardData?.agent_status != 'published' || tabName == 'in-app')
              }
              className="m-0"
            >
              {primaryActionLabel}
            </Button>
          }
        </Modal.Footer>
      </Modal>
      {/* Model Ends Here */}
    </>
  );
};

const AppStat = ({
  icon: Icon,
  value,
  label,
  count = 0,
  emptyText,
  showCount,
  isMobile,
  title = '',
}) => {
  const hasValue = Number(value) > 0;
  const compactValue = formatCompactNumber(value);
  const compactCount = formatCompactNumber(count);

  return (
    <div
      className={`app-stat d-flex align-items-center w-50 ${!isMobile ? 'p-1 rounded' : ''}`}
      title={title}
    >
      <Icon className="ms-2 me-2 mt-1" />

      {hasValue ? (
        <div className="d-flex align-items-center">
          <span className="me-1 rating-text">{compactValue}</span>

          {showCount ? (
            <span className="interaction-meta total-rating">
              ({compactCount} {label})
            </span>
          ) : (
            <span className="interaction-label total-rating">{label}</span>
          )}
        </div>
      ) : (
        <span className="interaction-empty">{emptyText}</span>
      )}
    </div>
  );
};
